import React, { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../auth/AuthProvider';
import { getBaseUrl, setBaseUrl } from '../../lib/api';
import { DEFAULT_API_URL, DEMO } from '../../lib/config';
import { errorMessage, fieldErrors } from '../../lib/errors';
import { useBreakpoint } from '../../lib/hooks';
import { KEYS, storage } from '../../lib/storage';
import { Alert, Button, Icon, IconButton, radius, Text, TextField, useTheme } from '../../ui';

function Hero() {
  return (
    <View style={{ flex: 1, backgroundColor: '#4338ca', padding: 48, justifyContent: 'space-between' }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Icon name="logoMark" size={22} color="#fff" strokeWidth={3} />
        <Text weight="bold" color="#fff" size={16}>
          Helios HRMS
        </Text>
      </View>
      <View style={{ gap: 18, maxWidth: 460 }}>
        <Text color="#fff" size={34} weight="bold" style={{ lineHeight: 40, letterSpacing: -0.8 }}>
          People, attendance, leave and India payroll — in one place.
        </Text>
        {['Statutory-accurate PF, ESI, PT, LWF, TDS & Form 16', 'Configurable approvals and leave policies', 'Keyboard-first admin screens — press Ctrl/⌘ K anywhere', 'The same app on web, Android and iOS'].map((l) => (
          <View key={l} style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
            <Icon name="checkCircle" size={16} color="#c7d2fe" />
            <Text color="#e0e7ff">{l}</Text>
          </View>
        ))}
      </View>
      <Text color="#c7d2fe" size={12}>
        © {new Date().getFullYear()}
      </Text>
    </View>
  );
}

export function Login() {
  const { login } = useAuth();
  const { colors, dark, toggle } = useTheme();
  const { isTablet } = useBreakpoint();
  const insets = useSafeAreaInsets();
  const [form, setForm] = useState({ tenant_code: storage.peek(KEYS.lastTenant) ?? '', email: storage.peek(KEYS.lastEmail) ?? '', password: '' });
  const [server, setServer] = useState(getBaseUrl());
  const [showServer, setShowServer] = useState(getBaseUrl() !== DEFAULT_API_URL);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const fe = fieldErrors(err);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (server.trim() && server.trim() !== getBaseUrl()) await setBaseUrl(server);
      await login(form);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.bg }}>
      {isTablet && <Hero />}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 20, paddingTop: insets.top + 20, paddingBottom: insets.bottom + 20 }} keyboardShouldPersistTaps="handled">
        <View style={{ width: '100%', maxWidth: 400, gap: 14 }} testID="login-form">
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <View style={{ flex: 1 }}>
              {!isTablet && (
                <View style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Icon name="logoMark" size={20} color={colors.onPrimary} strokeWidth={3} />
                </View>
              )}
              <Text variant="h1">Sign in</Text>
              <Text variant="muted">Use your company code and work email.</Text>
            </View>
            <IconButton icon={dark ? 'sun' : 'moon'} label="Toggle theme" onPress={toggle} />
          </View>
          {!!err && !Object.keys(fe).length && <Alert tone="danger">{errorMessage(err)}</Alert>}
          <TextField label="Company code" required value={form.tenant_code} onChangeText={set('tenant_code')} placeholder="acme" autoCapitalize="none" autoCorrect={false} error={fe.tenant_code} testID="login-tenant" />
          <TextField label="Email" required value={form.email} onChangeText={set('email')} placeholder="you@company.com" autoCapitalize="none" autoCorrect={false} keyboardType="email-address" autoComplete="email" error={fe.email} testID="login-email" />
          <TextField label="Password" required value={form.password} onChangeText={set('password')} secureTextEntry autoComplete="password" error={fe.password} onSubmitEditing={submit} returnKeyType="go" testID="login-password" />
          <Button title="Sign in" variant="primary" size="lg" block loading={busy} onPress={submit} testID="login-submit" />
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: 10, borderRadius: radius.md, borderWidth: 1, borderStyle: 'dashed', borderColor: colors.border, backgroundColor: colors.surface2 }}>
            <Text variant="small" style={{ flex: 1, minWidth: 200 }}>
              Demo: company <Text weight="semibold" size={12}>{DEMO.tenant}</Text>, <Text weight="semibold" size={12}>{DEMO.email}</Text> / <Text weight="semibold" size={12}>{DEMO.password}</Text>
            </Text>
            <Button size="sm" variant="ghost" title="Fill" onPress={() => setForm({ tenant_code: DEMO.tenant, email: DEMO.email, password: DEMO.password })} testID="login-fill-demo" />
          </View>
          <Pressable onPress={() => setShowServer((s) => !s)} accessibilityRole="button" style={{ flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start' }}>
            <Icon name="server" size={14} color={colors.muted} />
            <Text variant="small">Server: {getBaseUrl()}</Text>
            <Icon name={showServer ? 'chevronUp' : 'chevronDown'} size={12} color={colors.muted} />
          </Pressable>
          {showServer && (
            <View style={{ gap: 8 }}>
              <TextField label="API server URL" value={server} onChangeText={setServer} autoCapitalize="none" autoCorrect={false} keyboardType="url" hint="Android emulator: http://10.0.2.2:8000/api · devices: http://<your LAN IP>:8000/api" testID="login-server" />
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Button size="sm" title="Save server" onPress={async () => { await setBaseUrl(server); setServer(getBaseUrl()); }} />
                <Button size="sm" variant="ghost" title="Reset to default" onPress={async () => { await setBaseUrl(null); setServer(getBaseUrl()); }} />
              </View>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}
